import { useState } from 'react';
import { ShoppingCart, RefreshCcw, BookUser, MapPin, CreditCard, KeyRound, Gift, LogOut, CalendarSync, PackageCheck, Truck, Trash2, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { performLogout } from '../../store/thunk/authThunk';
import { selectUser } from '../../store/reducer/authSlice';
import toast from 'react-hot-toast';

// Keys that must never be cleared (auth + app flags)
const PROTECTED_KEYS = ['token', 'isMobileApp'];

const getContentUserMenu = (userRole) => {
    const adminOrdersMenu = {
        id: 0,
        title: 'Admin Orders',
        path: '/orders-list?type=delivery-partner',
        description: 'Manage all orders.',
        icon: <PackageCheck className='lg:w-7 lg:h-7 md:w-6 md:h-6' />,
    };

    const deliveryPartnerOrdersMenu = {
        id: 0,
        title: 'Delivery Partner Orders',
        path: '/partner-orders',
        description: 'Manage partner orders.',
        icon: <Truck className='lg:w-7 lg:h-7 md:w-6 md:h-6' />,
    };

    const commonMenu = [
    {
        id: 1,
        title: 'My Orders',
        path: '/orders',
        description: 'Keep track of your orders.',
        icon: <ShoppingCart className='lg:w-7 lg:h-7 md:w-6 md:h-6' />,
    },
    {
        id: 2,
        title: 'My Subscriptions',
        path: '/subscriptions',
        description: 'Manage your subscriptions',
        icon: <CalendarSync  className='lg:w-7 lg:h-7 md:w-6 md:h-6' />,
    },
    {
        id: 3,
        title: 'Billing Address',
        path: '/billing-address',
        description: 'Update your billing address.',
        icon: <BookUser className='lg:w-7 lg:h-7 md:w-6 md:h-6' />,
    },
    {
        id: 4,
        title: 'Delivery Method',
        path: '/delivery-method',
        description: 'Update your delivery method.',
        icon: <MapPin className='lg:w-7 lg:h-7 md:w-6 md:h-6' />,
    },
    {
        id: 5,
        title: 'Payment Details',
        path: '/payment-details',
        description: 'Update card and PayPal.',
        icon: <CreditCard className='lg:w-7 lg:h-7 md:w-6 md:h-6' />,
    },
    {
        id: 6,
        title: 'Password Settings',
        path: '/password-settings',
        description: 'Change your password.',
        icon: <KeyRound className='lg:w-7 lg:h-7 md:w-6 md:h-6' />,
    },
    {
        id: 7,
        title: 'Rewards',
        path: '/rewards',
        description: 'Rewards from your orders.',
        icon: <Gift className='lg:w-7 lg:h-7 md:w-6 md:h-6' />,
    },
    {
        id: 8,
        title: 'Sign out',
        path: '/signout',
        description: 'Halalfull Sign out.',
        icon: <LogOut className='lg:w-7 lg:h-7 md:w-6 md:h-6' />,
    },
];

    // Add role-specific menu at the beginning
    if (userRole === 1) {
        return [adminOrdersMenu, ...commonMenu];
    } else if (userRole === 6) {
        return [deliveryPartnerOrdersMenu, ...commonMenu];
    }

    return commonMenu;
};

const SideTabMenu = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector(selectUser);
    const ContentUserMenu = getContentUserMenu(user?.roles);
    const [showClearCacheDialog, setShowClearCacheDialog] = useState(false);

    const handleItemClick = (item) => {
        if (item.path === '/signout') {
            dispatch(performLogout()).then(() => {
                navigate('/signin', { replace: true });
            });
            return;
        }

        if (item.path) {
            navigate(item.path);
        }
    };

    const handleClearCache = () => {
        // Preserve protected keys before clearing
        const preserved = {};
        PROTECTED_KEYS.forEach((key) => {
            const val = localStorage.getItem(key);
            if (val !== null) preserved[key] = val;
        });

        localStorage.clear();
        sessionStorage.clear();

        // Restore protected keys
        Object.entries(preserved).forEach(([key, val]) => {
            localStorage.setItem(key, val);
        });

        setShowClearCacheDialog(false);
        toast.success('Cache cleared successfully');
    };

    return (
        <>
            <div className='w-full bg-[#f9f9f9] border border-neutral-200 rounded-lg shadow-[0_3px_10px_rgb(0,0,0,0.2)]'>
                <div className='space-y-1 p-2'>
                    {ContentUserMenu.map((item) => {
                        const isActive = location.pathname.startsWith(item.path || '');

                        return (
                            <div
                                key={item.id}
                                className={`flex items-center px-2 md:px-0 lg:px-2 py-3 cursor-pointer transition-all duration-200 hover:bg-gray-50 border-b border-gray-200 last:border-b-0 ${
                                    isActive ? '' : ''
                                }`}
                                onClick={() => handleItemClick(item)}
                            >
                                <div className='flex items-center justify-center w-10 h-10 rounded-lg md:mr-0 lg:mr-4'>
                                    <div className='text-primary-text'>{item.icon}</div>
                                </div>

                                <div className='flex-1 min-w-0'>
                                    <h3
                                        className={`md:text-md lg:text-base font-medium tracking-[0.7px] ${
                                            isActive ? 'text-primary-text' : ''
                                        }`}
                                    >
                                        {item.title}
                                    </h3>
                                    <p className='text-xs text-gray-400 leading-relaxed tracking-[0.7px]'>
                                        {item.description}
                                    </p>
                                </div>
                            </div>
                        );
                    })}

                    {/* Clear Cache Button */}
                    <div
                        className='flex items-center px-2 md:px-0 lg:px-2 py-3 cursor-pointer transition-all duration-200 hover:bg-gray-50 mt-1'
                        onClick={() => setShowClearCacheDialog(true)}
                    >
                        <div className='flex items-center justify-center w-10 h-10 rounded-lg md:mr-0 lg:mr-4'>
                            <Trash2 className='lg:w-7 lg:h-7 md:w-6 md:h-6 text-gray-400' />
                        </div>
                        <div className='flex-1 min-w-0'>
                            <h3 className='md:text-md lg:text-base font-medium tracking-[0.7px] text-gray-500'>
                                Clear Cache
                            </h3>
                            <p className='text-xs text-gray-400 leading-relaxed tracking-[0.7px]'>
                                Clear app data (keeps login).
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation Dialog */}
            {showClearCacheDialog && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4'>
                    <div className='bg-white rounded-xl shadow-xl w-full max-w-sm p-6'>
                        <div className='flex items-start justify-between mb-4'>
                            <div className='flex items-center gap-3'>
                                <div className='w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0'>
                                    <Trash2 size={18} className='text-orange-500' />
                                </div>
                                <h3 className='font-semibold text-gray-900'>Clear Cache</h3>
                            </div>
                            <button
                                onClick={() => setShowClearCacheDialog(false)}
                                className='text-gray-400 hover:text-gray-600'
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <p className='text-sm text-gray-600 mb-2'>
                            This will clear stored app data such as saved promo codes and temporary data.
                        </p>
                        <p className='text-sm font-medium text-green-700 bg-green-50 rounded-lg px-3 py-2 mb-5'>
                            Your login credentials will <span className='font-bold'>not</span> be affected.
                        </p>

                        <div className='flex gap-3'>
                            <button
                                onClick={() => setShowClearCacheDialog(false)}
                                className='flex-1 px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors'
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleClearCache}
                                className='flex-1 px-4 py-2 rounded-lg bg-primary-100 text-white text-sm font-medium hover:bg-red-800 transition-colors'
                            >
                                Clear Cache
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
export default SideTabMenu;
